import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight } from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 border-r border-white/10 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7017673/pexels-photo-7017673.jpeg"
          alt="NATYE"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo size={40} textClassName="font-heading font-black tracking-[0.14em] uppercase text-lg bg-gradient-to-br from-[#FDE7A8] via-[#E8B527] to-[#B8860B] bg-clip-text text-transparent" />
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              Studio Community Management
            </p>
            <h1 className="font-heading text-5xl font-black tracking-tight uppercase leading-[0.95]">
              L'atelier
              <br />
              de vos
              <br />
              <span className="text-accent">réseaux.</span>
            </h1>
          </div>
          <p className="text-sm text-muted-foreground max-w-sm">
            Gère plusieurs marques, importe leurs visuels, génère des posts Instagram avec l'IA
            et planifie — le tout depuis un seul studio sécurisé.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-10">
            <Logo size={36} textClassName="font-heading font-black tracking-[0.14em] uppercase text-base bg-gradient-to-br from-[#FDE7A8] via-[#E8B527] to-[#B8860B] bg-clip-text text-transparent" />
          </div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
            Accès sécurisé
          </p>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-8">
            Connexion
          </h2>

          <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
            Email
          </label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 mb-5 outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
            placeholder="Votre email"
          />

          <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
            Mot de passe
          </label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 mb-6 outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
            placeholder="••••••••"
          />

          {error && (
            <p data-testid="login-error" className="text-accent text-sm mb-4">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="group w-full bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm py-3.5 rounded-sm flex items-center justify-center gap-2 hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Entrer
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
